import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Avatar
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Cookies from 'js-cookie';
import { apiUrlPrefix } from '../../../config';
import { useQueryClient } from '@tanstack/react-query';
import convertHeic from '../helper-methods/convertHEIC';
import imageCompression from 'browser-image-compression';

const ChangeProfilePictureWidget = ({ closeMenu, username, selectedItem, nonSelectedItem, selected }) => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const queryClient = useQueryClient();

  const handleClickOpen = (e) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (closeMenu) closeMenu();
  };

const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    let fileToCompress = file;

    // 1. If it's HEIC, convert it to a Blob first
    if (file.name.toLowerCase().endsWith(".heic")) {
      const fileUrl = URL.createObjectURL(file);
      const convertedUrl = await convertHeic(fileUrl);
      const response = await fetch(convertedUrl);
      const blob = await response.blob();
      
      // Prepare the converted blob for compression
      fileToCompress = new File([blob], file.name.replace(/\.heic$/i, ".jpg"), { type: "image/jpeg" });
      URL.revokeObjectURL(fileUrl);
      URL.revokeObjectURL(convertedUrl);
    }

    // 2. Compression Settings
    const options = {
      maxSizeMB: 0.5,          // Max file size is 500KB
      maxWidthOrHeight: 1024, // Resizes larger images to 1024px max
      useWebWorker: true,
      fileType: "image/jpeg"
    };

    // 3. Execute Compression
    const compressedBlob = await imageCompression(fileToCompress, options);
    
    // 4. Convert Blob back to File for your FormData
    const finalFile = new File([compressedBlob], fileToCompress.name, {
      type: "image/jpeg",
    });

    setSelectedFile(finalFile);
    setPreviewUrl(URL.createObjectURL(finalFile));

  } catch (error) {
    console.error("Image processing failed:", error);
  }
};

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const userId = localStorage.getItem('userId');
      const fileExtension = selectedFile.name.split(".").pop();
      var profileFile = selectedFile; //Able to change details in file
      const usernameTruncated = username.replaceAll(' ', '');
      const newFileName = `${usernameTruncated}.${fileExtension}`;

      // 1. Create FormData (This is the container for binary files)
      const formData = new FormData();

      // 2. Append your data (Keys must match your C# UserInformation properties)
      formData.append('ID', userId);
      formData.append('Name', username);
      formData.append('profilePicture', fileExtension);

      // This sends the ACTUAL pixels/bytes, not just the name
      formData.append('profilePictureData', profileFile, newFileName);

      // 3. Send the request
      const response = await fetch(apiUrlPrefix + "MongoDb/UploadPicture", {
        method: 'POST',
        credentials: 'include',
        // IMPORTANT: Remove 'Content-Type': 'application/json'
        // The browser will set it to 'multipart/form-data' automatically
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Invalid credentials or permissions.');
        }
        throw new Error('Network response was not ok');
      }
      //If it makes it to this point the response is OK
      const data = await response.json();
      handleClose();
      return data;
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  return (
    <>
      <Typography
        id="changeProfilePictureButton"
        color="none"
        fontSize={'18px'}
        onClick={ handleClickOpen }
        sx={{ ...(selected === 3) ? selectedItem : nonSelectedItem }}
      >
        Change Profile Picture
      </Typography>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        // This prevents the dialog from closing if they click outside accidentally
        disableEscapeKeyDown
      >
        <DialogTitle>Update Profile Picture</DialogTitle>
        <DialogContent>
          <Box
            // STOP PROPAGATION HERE
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 3,
            }}
          >
            {previewUrl ? (
              <Avatar src={previewUrl} sx={{ width: 100, height: 100 }} />
            ) : (
              <CloudUploadIcon sx={{ fontSize: 48, color: 'gray' }} />
            )}

            <Typography variant="body2" color="textSecondary">
              {selectedFile ? selectedFile.name : "Please choose an image"}
            </Typography>

            {/* The Hidden Input */}
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="upload-photo-input" // Unique ID
              type="file"
              onChange={handleFileChange}
            />

            {/* The Label/Button Trigger */}
            <label htmlFor="upload-photo-input">
              <Button
                variant="outlined"
                component="span"
                // Explicitly stop propagation on the button click as well
                onClick={(e) => e.stopPropagation()}
              >
                Choose File
              </Button>
            </label>

            <Typography variant="caption">
              {selectedFile ? selectedFile.name : "No file selected"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangeProfilePictureWidget;